<template>
  <div ref="fabRef" class="stage-assistant-fab" :class="{ 'stage-assistant-fab--open': open, 'stage-assistant-fab--dragging': dragActive }" :style="fabStyle">
    <transition name="stage-assistant-panel">
      <section v-if="open" class="stage-assistant-fab__panel">
        <div class="stage-assistant-fab__panel-glow" />
        <header class="stage-assistant-fab__header" @pointerdown="handleHeaderPointerDown">
          <div>
            <span class="stage-assistant-fab__eyebrow">{{ stageLabel }}助手</span>
            <strong>{{ title }}</strong>
            <p>{{ description }}</p>
          </div>
          <button type="button" class="stage-assistant-fab__close" aria-label="收起助手" @click="emit('update:open', false)">
            ×
          </button>
        </header>

        <div class="stage-assistant-fab__messages">
          <article
            v-for="message in messages"
            :key="message.id"
            class="stage-assistant-fab__message"
            :class="`stage-assistant-fab__message--${message.role}`"
          >
            <span class="stage-assistant-fab__message-role">{{ message.role === 'user' ? '你' : '助手' }}</span>
            <p>{{ message.content }}</p>
          </article>
          <div v-if="loading" class="stage-assistant-fab__loading">
            <span />
            <span />
            <span />
          </div>
        </div>

        <div class="stage-assistant-fab__composer">
          <el-input
            :model-value="draft"
            type="textarea"
            :rows="4"
            resize="none"
            maxlength="600"
            show-word-limit
            placeholder="直接说你的修改意图，例如：把脚本语气改得更克制，或者把当前场景的镜头描述写得更电影化。"
            @update:model-value="emit('update:draft', String($event || ''))"
          />
          <div class="stage-assistant-fab__composer-actions">
            <small>{{ helperText }}</small>
            <el-button type="primary" :loading="loading" :disabled="!draft.trim()" @click="emit('submit')">
              发送
            </el-button>
          </div>
        </div>
      </section>
    </transition>

    <button
      type="button"
      class="stage-assistant-fab__trigger"
      :class="{ 'stage-assistant-fab__trigger--open': open }"
      @pointerdown="handleTriggerPointerDown"
      @click="handleTriggerClick"
    >
      <span class="stage-assistant-fab__trigger-ring" />
      <span class="stage-assistant-fab__trigger-text">
        <small>AI · {{ stageLabel }}</small>
        <strong>{{ open ? '收起助手' : '打开助手' }}</strong>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'

import type { StageAssistantMessage } from './stageAssistantTypes'

const props = defineProps<{
  open: boolean
  draft: string
  loading: boolean
  stageLabel: string
  title: string
  description: string
  helperText: string
  messages: StageAssistantMessage[]
}>()

const emit = defineEmits<{
  (event: 'update:open', value: boolean): void
  (event: 'update:draft', value: string): void
  (event: 'submit'): void
}>()

const FAB_EDGE_PADDING = 12
const fabRef = ref<HTMLElement | null>(null)
const dragOffsetX = ref(0)
const dragOffsetY = ref(0)
const dragActive = ref(false)
const dragMoved = ref(false)
const dragPointerId = ref<number | null>(null)
const dragStartClientX = ref(0)
const dragStartClientY = ref(0)
const dragStartOffsetX = ref(0)
const dragStartOffsetY = ref(0)
const dragBaseLeft = ref(0)
const dragBaseTop = ref(0)
const dragWidth = ref(0)
const dragHeight = ref(0)

const fabStyle = computed(() => ({
  '--stage-assistant-drag-x': `${dragOffsetX.value}px`,
  '--stage-assistant-drag-y': `${dragOffsetY.value}px`,
}))

function shouldIgnoreDragStart(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false
  }
  return Boolean(target.closest('button, textarea, input, select, .el-input, .el-textarea, .el-button, .el-select, .el-slider'))
}

function clampDragOffset(nextOffsetX: number, nextOffsetY: number) {
  const minOffsetX = FAB_EDGE_PADDING - dragBaseLeft.value
  const maxOffsetX = window.innerWidth - dragWidth.value - FAB_EDGE_PADDING - dragBaseLeft.value
  const minOffsetY = FAB_EDGE_PADDING - dragBaseTop.value
  const maxOffsetY = window.innerHeight - dragHeight.value - FAB_EDGE_PADDING - dragBaseTop.value

  return {
    x: Math.min(Math.max(nextOffsetX, minOffsetX), maxOffsetX),
    y: Math.min(Math.max(nextOffsetY, minOffsetY), maxOffsetY),
  }
}

function stopDragging() {
  dragActive.value = false
  dragPointerId.value = null
  window.removeEventListener('pointermove', handlePointerMove)
  window.removeEventListener('pointerup', handlePointerUp)
  window.removeEventListener('pointercancel', handlePointerUp)
}

function handlePointerMove(event: PointerEvent) {
  if (dragPointerId.value !== event.pointerId) {
    return
  }

  const nextOffsetX = dragStartOffsetX.value + (event.clientX - dragStartClientX.value)
  const nextOffsetY = dragStartOffsetY.value + (event.clientY - dragStartClientY.value)
  const clamped = clampDragOffset(nextOffsetX, nextOffsetY)
  dragOffsetX.value = clamped.x
  dragOffsetY.value = clamped.y

  if (!dragMoved.value) {
    const movedDistance = Math.abs(event.clientX - dragStartClientX.value) + Math.abs(event.clientY - dragStartClientY.value)
    dragMoved.value = movedDistance > 6
  }
}

function handlePointerUp(event: PointerEvent) {
  if (dragPointerId.value !== event.pointerId) {
    return
  }
  stopDragging()
}

function beginDrag(event: PointerEvent) {
  if (event.button !== 0 || !fabRef.value) {
    return
  }

  const rect = fabRef.value.getBoundingClientRect()
  dragActive.value = true
  dragMoved.value = false
  dragPointerId.value = event.pointerId
  dragStartClientX.value = event.clientX
  dragStartClientY.value = event.clientY
  dragStartOffsetX.value = dragOffsetX.value
  dragStartOffsetY.value = dragOffsetY.value
  dragBaseLeft.value = rect.left - dragOffsetX.value
  dragBaseTop.value = rect.top - dragOffsetY.value
  dragWidth.value = rect.width
  dragHeight.value = rect.height

  window.addEventListener('pointermove', handlePointerMove)
  window.addEventListener('pointerup', handlePointerUp)
  window.addEventListener('pointercancel', handlePointerUp)
}

function handleTriggerPointerDown(event: PointerEvent) {
  beginDrag(event)
}

function handleHeaderPointerDown(event: PointerEvent) {
  if (shouldIgnoreDragStart(event.target)) {
    return
  }
  beginDrag(event)
}

function handleTriggerClick() {
  if (dragMoved.value) {
    dragMoved.value = false
    return
  }
  emit('update:open', !props.open)
}

onBeforeUnmount(() => {
  stopDragging()
})
</script>

<style scoped>
.stage-assistant-fab {
  position: fixed;
  right: 18px;
  top: 52%;
  transform: translate(var(--stage-assistant-drag-x, 0px), calc(-50% + var(--stage-assistant-drag-y, 0px)));
  z-index: 120;
  display: grid;
  justify-items: end;
  gap: 14px;
  pointer-events: none;
}

.stage-assistant-fab > * {
  pointer-events: auto;
}

.stage-assistant-fab--dragging {
  user-select: none;
}

.stage-assistant-fab__panel {
  position: relative;
  width: min(392px, calc(100vw - 32px));
  max-height: min(76vh, 720px);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.88) 36%, rgba(242, 250, 247, 0.9) 100%);
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.22), 0 10px 28px rgba(20, 184, 166, 0.12);
  backdrop-filter: blur(20px);
}

.stage-assistant-fab__panel-glow {
  position: absolute;
  inset: auto auto -90px -80px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(20, 184, 166, 0.18), transparent 70%);
  pointer-events: none;
}

.stage-assistant-fab__header {
  position: relative;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 18px 16px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  cursor: grab;
}

.stage-assistant-fab--dragging .stage-assistant-fab__header,
.stage-assistant-fab--dragging .stage-assistant-fab__trigger {
  cursor: grabbing;
}

.stage-assistant-fab__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 5px 9px;
  border-radius: 999px;
  background: rgba(15, 118, 110, 0.08);
  color: #0f766e;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stage-assistant-fab__header strong {
  display: block;
  margin-top: 8px;
  color: #0f172a;
  font-size: 17px;
  line-height: 1.2;
}

.stage-assistant-fab__header p {
  margin: 6px 0 0;
  color: #5b6b7f;
  font-size: 13px;
  line-height: 1.65;
}

.stage-assistant-fab__close {
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
  font-size: 24px;
  cursor: pointer;
}

.stage-assistant-fab__messages {
  position: relative;
  display: grid;
  align-content: start;
  gap: 10px;
  min-height: 0;
  padding: 14px 18px;
  overflow: auto;
}

.stage-assistant-fab__message {
  display: grid;
  gap: 6px;
  max-width: 92%;
  padding: 12px 14px;
  border-radius: 18px;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
}

.stage-assistant-fab__message--assistant {
  justify-self: start;
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.96), rgba(236, 252, 243, 0.94));
  color: #11332d;
}

.stage-assistant-fab__message--user {
  justify-self: end;
  background: linear-gradient(135deg, #12344a, #0f766e);
  color: #f8fafc;
}

.stage-assistant-fab__message-role {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.72;
}

.stage-assistant-fab__message p {
  margin: 0;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.62;
}

.stage-assistant-fab__loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
}

.stage-assistant-fab__loading span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(15, 118, 110, 0.5);
  animation: stage-assistant-loading 1.2s infinite ease-in-out;
}

.stage-assistant-fab__loading span:nth-child(2) {
  animation-delay: 0.15s;
}

.stage-assistant-fab__loading span:nth-child(3) {
  animation-delay: 0.3s;
}

.stage-assistant-fab__composer {
  display: grid;
  gap: 10px;
  padding: 14px 18px 18px;
  border-top: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.72);
}

.stage-assistant-fab__composer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.stage-assistant-fab__composer-actions small {
  color: #64748b;
  font-size: 12px;
  line-height: 1.6;
}

.stage-assistant-fab__trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  min-width: 172px;
  padding: 14px 16px 14px 14px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #12344a, #0f766e 68%, #14b8a6);
  color: #f8fafc;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.28);
  cursor: pointer;
  overflow: hidden;
  touch-action: none;
}

.stage-assistant-fab__trigger::before {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: inherit;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0.02));
  pointer-events: none;
}

.stage-assistant-fab__trigger--open {
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.18);
}

.stage-assistant-fab__trigger-ring {
  position: relative;
  z-index: 1;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.32));
  box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.12);
}

.stage-assistant-fab__trigger-text {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: start;
  gap: 2px;
}

.stage-assistant-fab__trigger-text small {
  opacity: 0.74;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.stage-assistant-fab__trigger-text strong {
  font-size: 14px;
  letter-spacing: 0.01em;
}

.stage-assistant-panel-enter-active,
.stage-assistant-panel-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.stage-assistant-panel-enter-from,
.stage-assistant-panel-leave-to {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

@keyframes stage-assistant-loading {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .stage-assistant-fab {
    right: 12px;
    top: auto;
    bottom: 12px;
    transform: translate(var(--stage-assistant-drag-x, 0px), var(--stage-assistant-drag-y, 0px));
  }

  .stage-assistant-fab__panel {
    width: min(100vw - 24px, 420px);
    max-height: calc(100vh - 96px);
    border-radius: 24px;
  }

  .stage-assistant-fab__trigger {
    min-width: 148px;
    padding: 12px 16px;
  }
}
</style>